import dayjs from "dayjs";
import { eq } from "drizzle-orm";
import { Chart } from "~/components/metrika/Chart";
import { DropList } from "~/components/metrika/DropList";
import { Inventory } from "~/components/metrika/Inventory";
import { Market } from "~/components/metrika/Market";
import { PaperDoll } from "~/components/metrika/PaperDoll";
import { PlayerStats } from "~/components/metrika/PlayerStats";
import { PaymentForm } from "~/components/site/PaymentForm";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Heading2 } from "~/components/ui/Heading";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { homePage } from "~/util/titles";
import { character, loot, market } from "./exampleData";
import css from "./home.module.css";
import { logPageView } from "~/util/stats";

$PAGE({
  path: "subscription",
  loader: $LOADER?.(async (args) => {
    // Log
    logPageView(args);
    //
    const sess = args.req.sess;

    const user = sess
      ? await pg.db
          .select({
            login: pg.users.login,
            timezone: pg.users.timezone,
            subscriptionExpiry: pg.subscriptions.endsAt,
          })
          .from(pg.users)
          .where(eq(pg.users.id, sess.user.id))
          .leftJoin(pg.subscriptions, eq(pg.subscriptions.userId, pg.users.id))
          .then((res) => res.at(0))
      : undefined;

    return { user, authUser: args.req.sess?.user };
  }),
  meta: (cx) => {
    const title = "Subscription";

    return {
      title: `${title} - ${homePage(cx.realm)}`,
      description: "Subscription L2hub",
    };
  },
  render: (props) => {
    const { data } = props;
    const t = useT();
    const turl = useTurl();
    const title = t({ en: "Subscription", ru: "Подписка" });
    const timezone = dayjs.tz.guess();

    return (
      <div className="content">
        <div className={css.root}>
          <div className="columns">
            <div className="column is-three-quarters">
              <Breadcrumb items={[{ title, url: turl("/subscribe") }]} />
              <PageTitle>{title}</PageTitle>
              <p>
                {t({
                  en: "Get access to the statistics of your Lineage 2 game character, look at inventory, warehouse, information on killed monsters and rewards at any time.",
                  ru: "Получите доступ к статистике вашего игрового персонажа Lineage 2. Смотри свой инвентарь, склад, информацию об убитых монстрах и наградах в любое время.",
                })}
              </p>
              <Heading2>{t({ en: "Character", ru: "Персонаж" })}</Heading2>
              <p>
                {t({
                  en: "The character's doll and the stats window.",
                  ru: "Кукла персонажа и окно статистики.",
                })}
              </p>
              <div className="columns">
                <div className="column">
                  <PaperDoll gver={"c3"} data={character.doll} />
                </div>
                <div className="column">
                  <PlayerStats character={character.stats} />
                </div>
              </div>
              <Heading2>
                {t({ en: "Inventory and Warehouse", ru: "Инвентарь и склад" })}
              </Heading2>
              <p>
                {t({
                  en: "Look at your inventory and warehouse at any time.",
                  ru: "Смотри своё инвентарь и склад в любое время.",
                })}
              </p>
              <div className="columns">
                <div className="column">
                  <Inventory
                    title="Inventory"
                    data={character.inventory}
                    height={300}
                  />
                </div>
                <div className="column">
                  <Inventory
                    title="Warehouse"
                    data={character.warehouse}
                    height={300}
                  />
                </div>
              </div>
              <Heading2>Statistics</Heading2>
              <p>
                {t({
                  en: "See the statistics for the required period.",
                  ru: "Смотрите свою статистику за необходимый период.",
                })}
              </p>
              <div className="columns">
                <div
                  className="column"
                  style={{ display: "flex", flexDirection: "column", gap: 20 }}
                >
                  <Chart
                    data={loot
                      .filter(
                        (f) => f.type === "ITEM" && f.item && f.item.id === 57
                      )
                      .map((d) => ({ ...d, date: dayjs(d.date).toDate() }))}
                    range="24h"
                    color="gold"
                    prefix="Adena"
                    title="Adena Chart"
                    timezone={timezone}
                  />
                  <Chart
                    data={loot
                      .filter((f) => f.type === "EXP")
                      .map((d) => ({ ...d, date: dayjs(d.date).toDate() }))}
                    range="24h"
                    color="cornflowerblue"
                    prefix="EXP"
                    title="Experience Chart"
                    timezone={timezone}
                  />

                  <Chart
                    data={loot
                      .filter((f) => f.type === "SP")
                      .map((d) => ({ ...d, date: dayjs(d.date).toDate() }))}
                    range="24h"
                    color="aliceblue"
                    prefix="SP"
                    title="Skill Poins Chart"
                    timezone={timezone}
                  />
                </div>
                <div className="column">
                  <DropList
                    data={loot
                      .filter((f) => f.type === "ITEM")
                      .map((d) => ({ ...d, date: dayjs(d.date).toDate() }))}
                    timezone={timezone}
                  />
                </div>
              </div>
              <Heading2>{t({ en: "Market Explorer", ru: "Рынок" })}</Heading2>
              <p>
                {t({
                  en: "Convenient search by the merchants you have visited.",
                  ru: "Удобный поиск по продавцам, которых вы посетили.",
                })}
              </p>
              <Market data={market} height={300} />
            </div>
            <div className="column" style={{ position: "relative" }}>
              <div className={css.sticky}>
                <PaymentForm user={data.user} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
